import React, { useMemo, useRef, useState, useEffect, useCallback, startTransition } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { slugs } from '@/constants/slugs'
import { AppFeatures } from '@/experimentation'
import { FaqSection } from '@/organisms/FaqSection'
import { BackgroundPosition, WatchProjectHero } from '@/organisms/WatchProjectHero'
import { WatchProjectTabs } from '@/organisms/WatchProjectTabs'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { LightFranchise } from '@/services/FranchiseService'
import { isSellingTickets } from '@/services/PhaseManagerService'
import { getIsGeoblocked, Project, ProjectType, SeasonLinkMeta } from '@/services/ProjectsService'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { Story } from '@/services/StoriesService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { processWatchablesToMediasWithFiltering, Media } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { getProjectTheme } from '@/utils/project-themes'
import { useTranslate } from '@/utils/translate/translate-client'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { WatchProjectShareSeriesModal } from '@/views/WatchProjectView/WatchProjectShareSeriesModal'
import { useWatchProjectData } from '@/views/WatchProjectView/useWatchProjectData'
import { useGuildTickets } from '../TicketCheckoutViews/hooks/useGuildTickets'
import { FranchiseFilmSeriesCTA } from './FranchiseFilmSeriesCTA'
import { WatchProjectContext } from './WatchProjectContext'
import { useFadeBackgroundOnScroll } from './useFadeBackgroundOnScroll'
import { useWatchProject } from './useWatchProject'
import { getMovieEpisodeSharePath } from './utils'

export interface WatchProjectViewProps {
  currentlyFunding?: boolean
  discoveryPosterLandscapeCloudinaryPath: string
  name: string
  projectType: ProjectType
  shopifyConfig?: ShopifyStoreConfig
  singleStory?: boolean
  slug: string
  story?: Story
  theaterBackgroundPosition?: BackgroundPosition
  theaterDescription: string
  seasons?: SeasonLinkMeta[]
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  contentfulProjectTheme: Maybe<ProjectTheme>
  isProjectInEarlyAccess?: boolean
  projectData: Project
  isGuildMember?: boolean
  isLoggedIn?: boolean
  franchise?: LightFranchise
  hasWatchableReasons?: boolean
  activeSweepstake?: boolean
  skipsEnabled?: boolean
  experiments?: Record<keyof AppFeatures, unknown>
}

export const WatchProjectView: React.FC<WatchProjectViewProps> = (props) => {
  const {
    discoveryPosterLandscapeCloudinaryPath,
    name,
    projectType,
    singleStory,
    story,
    slug,
    theaterBackgroundPosition,
    seasons = [],
    page,
    pageDataContext,
    contentfulProjectTheme,
    isProjectInEarlyAccess = false,
    projectData,
    shopifyConfig,
    franchise,
    hasWatchableReasons = false,
    activeSweepstake = false,
    skipsEnabled = false,
    experiments,
    isGuildMember = false,
  } = props
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { watchables } = useWatchProjectContext()
  const [primaryCtaGuid, setPrimaryCtaGuid] = useState<string | null>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const boundaryRef = useRef<HTMLDivElement>(null)
  const faqsRef = useRef<HTMLDivElement>(null)
  const [movieSpecialMedias, setMovieSpecialMedias] = useState<Media[]>([])
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  useFadeBackgroundOnScroll({ backgroundRef, boundaryRef })
  const numSeasons = projectData?.seasons?.length
  const { project } = useWatchProject({ projectSlug: slug, skip: numSeasons === 0 })
  const isGeoblockedProject = getIsGeoblocked(project ?? projectData)
  const canShowTickets = isSellingTickets(projectData?.primaryFlowPhases ?? [])
  const watchableReasons = useMemo(
    () => projectData?.title?.watchableReasons ?? [],
    [projectData?.title?.watchableReasons],
  )

  const ticketsDefaultData = { discountCodes: [], areGuildTicketsAvailable: false }

  const ticketsHookResult = useGuildTickets(canShowTickets ? slug : '')

  const { discountCodes, areGuildTicketsAvailable } = canShowTickets ? ticketsHookResult : ticketsDefaultData

  const handlePrimaryCtaGuidChange = useCallback((guid: string | null) => {
    setPrimaryCtaGuid(guid)
  }, [])

  useEffect(() => {
    const PROJECT_LOGO_SCROLL_THRESHOLD = 210
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const shouldShow = window.scrollY > PROJECT_LOGO_SCROLL_THRESHOLD
          startTransition(() => {
            setUserHasScrolled((prev) => (prev !== shouldShow ? shouldShow : prev))
          })
          ticking = false
        })
        ticking = true
      }
    }

    if (typeof window !== 'undefined') {
      // Delay the initial scroll check to avoid hydration mismatch
      const timeoutId = setTimeout(() => {
        handleScroll()
      }, 0)
      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useWatchProjectData({
    data: projectData,
    skip: !seasons?.length,
    isGuildMember,
    isGeoblockedProject,
  })
  const projectTheme = getProjectTheme(slug) ?? {}
  const showFranchiseFilmSeriesCTA = false

  const hasEpisodesToShow = useMemo(() => {
    if (projectData?.projectType === 'movie' || projectData?.projectType === 'special') {
      return false
    }

    const titleSeasons = projectData?.title?.seasons?.edges
    if (titleSeasons && titleSeasons.length > 0) {
      const hasAnyEpisodes = titleSeasons.some((seasonEdge) => {
        const episodeCount = seasonEdge?.node?.episodes?.edges?.length ?? 0
        return episodeCount > 0
      })
      return hasAnyEpisodes
    }

    return false
  }, [projectData])

  const isMovieOrSpecial = !hasEpisodesToShow

  const shareGuid = useMemo(() => {
    if (projectType === 'series') {
      // Prefer the first season's first episode id if available via title.seasons connection
      const firstSeason = project?.title?.seasons?.edges?.[0]?.node
      const firstEpisodeId = firstSeason?.episodes?.edges?.[0]?.node?.id
      return firstEpisodeId ?? null
    }
    // Movie or special: take the first media item with a guid
    const firstWithGuid = movieSpecialMedias.find((m) => Boolean(m.guid))
    return firstWithGuid?.guid ?? null
  }, [projectType, project?.title?.seasons?.edges, movieSpecialMedias])

  const shareCtaPath = getMovieEpisodeSharePath({
    slug,
    guid: shareGuid,
    projectType,
    defaultCtaPath: '',
  })

  const showParentalControlsExperiment =
    slug === slugs.sketch ? true : experiments ? Boolean(experiments['family-friendly-audio-support']) : false

  // Fetch and prepare watchables for movies/specials when there are no seasons
  useEffect(() => {
    if (!watchables || watchables.length === 0) {
      setMovieSpecialMedias([])
      return
    }

    try {
      const mapped = processWatchablesToMediasWithFiltering(
        watchables,
        {
          projectName: projectData?.name,
          hasEarlyAccess: isProjectInEarlyAccess,
          projectType: projectData?.projectType,
          trailerText: t('trailer', 'Trailer'),
          locale,
        },
        isGeoblockedProject,
        isGuildMember,
      )
      setMovieSpecialMedias(mapped)
    } catch (error) {
      logger().error('Failed to process watchables', { slug, projectName: projectData?.name }, error)
      setMovieSpecialMedias([])
    }
  }, [
    watchables,
    projectData?.projectType,
    projectData?.name,
    t,
    locale,
    isGeoblockedProject,
    isGuildMember,
    isProjectInEarlyAccess,
    slug,
  ])

  return (
    <WatchProjectContext.Provider value={{ projectTheme, project: projectData }}>
      <div className={classNames('overflow-x-hidden bg-core-gray-950 text-white')}>
        <main>
          <WatchProjectHero
            className="mb-12"
            cloudinaryImgPath={
              discoveryPosterLandscapeCloudinaryPath
                ? `${discoveryPosterLandscapeCloudinaryPath}.webp`
                : '/v1706130971/angel-app/angel-studios/discovery_images/Theater-Home-Page-Banner_v2_2024-01-16_1920x1080.webp'
            }
            showTitle={name}
            slug={slug}
            theaterBackgroundPosition={theaterBackgroundPosition}
            isGuildMemberContent={isProjectInEarlyAccess}
            shopifyConfig={shopifyConfig}
            contentfulProjectTheme={contentfulProjectTheme}
            guildScore={projectData?.highestScore}
            isGuildMember={isGuildMember}
            isGeoBlockedProject={isGeoblockedProject}
            projectData={projectData}
            shareCtaPath={shareCtaPath}
            watchableReasons={watchableReasons}
            guildCodes={discountCodes}
            areGuildTicketsAvailable={areGuildTicketsAvailable}
            activeSweepstake={activeSweepstake}
            showParentalControlsExperiment={showParentalControlsExperiment}
            skipsEnabled={skipsEnabled}
            movieSpecialMedias={movieSpecialMedias}
            darkenBackground={userHasScrolled}
            onPrimaryCtaGuidChange={handlePrimaryCtaGuidChange}
          />
        </main>
        <div ref={boundaryRef}>
          <WatchProjectTabs
            projectData={projectData}
            loadingProjectData={false}
            slug={slug}
            singleStory={singleStory}
            story={story}
            page={page}
            pageDataContext={pageDataContext}
            isGuildMember={isGuildMember}
            franchise={franchise}
            hasWatchableReasons={hasWatchableReasons}
            isGeoblockedProject={isGeoblockedProject}
            movieSpecialMedias={movieSpecialMedias}
            showProjectLogo={userHasScrolled}
            primaryCtaGuid={primaryCtaGuid}
            isMovieOrSpecial={isMovieOrSpecial}
          />
        </div>
        <div className="relative h-48 w-full md:h-40">
          {/* Bottom -> Top Gradient  */}
          <div
            className={classNames('absolute inset-0 left-0 bottom-0 h-full w-full transition-opacity')}
            style={{
              background: 'linear-gradient(0deg, #141414 0%, rgba(20, 20, 20, 0.00) 100%)',
            }}
          />
        </div>
        <section id="faqs" className="relative scroll-mt-[73px] bg-core-gray-950 pb-40 pt-8 md:pb-20">
          <LazyHydrate whenVisible>
            <div ref={faqsRef}>
              <PaddedContainer fluid className="relative z-10">
                <FaqSection />
              </PaddedContainer>
            </div>
          </LazyHydrate>
        </section>
        <LazyHydrate whenVisible>
          <AppPromoSection
            containerClassName="pt-10"
            overrideAppImageUrl={'/v1715792164/angel-studios/landing/watch_amytime_-_image.webp'}
          />
        </LazyHydrate>
        {showFranchiseFilmSeriesCTA && isGuildMember && (
          <FranchiseFilmSeriesCTA project={projectData} franchise={franchise} />
        )}
      </div>
      <When condition={Boolean(isGuildMember && projectType === 'series')}>
        <WatchProjectShareSeriesModal slug={slug} />
      </When>
    </WatchProjectContext.Provider>
  )
}
